import { VueConstructor } from "vue";
import axios, { AxiosRequestConfig } from "axios";
// @ts-ignore
import config from "./Api-config";
// @ts-ignore
import NProgress from "nprogress";
// @ts-ignore
import hasha from "hasha";
// @ts-ignore
import storage from "../storage/storage";
import isReachable from "is-reachable";
import app from "@/store/app";
import qs from "query-string";
import Auth from "@/services/Auth";
import AnnouncementModule from "sentec-vues/modules/announcement/AnnouncementModule";
import {AnnouncementApp} from "sentec-vues/modules/announcement/enums/AnnouncementApp";
import {ApiCallParameter} from "sentec-core-ts";
import { normalizeErrorMessage, resolveTenantUrl } from "./Api.utils";
import { HotReadWindow, WRITE_HOT_READ_WINDOW_MS, MANUAL_HOT_READ_WINDOW_MS } from "./HotReadWindow";
import { payloadCarriesPin } from "./Api.pinGuard";

declare module "vue/types/vue" {
  interface Vue {
    $http: {
      get: (url: string, params?: any, headers?: any, runtimeSize?: ApiRuntimeSize,skipCancellation?: boolean, overrideBaseUrl?: string) => Promise<any>,
      post: (url: string, data?: any, headers?: any, runtimeSize?: ApiRuntimeSize, ignoreGlobalErrorHandler?: boolean) => Promise<any>,
      postv2: (param: ApiCallParameter) => Promise<any>,
      postDownload: (url: string, data?: any, headers?: any, runtimeSize?: ApiRuntimeSize, ignoreGlobalErrorHandler?: boolean) => Promise<any>,
      del: (url: string, data?: any, headers?: any, runtimeSize?: ApiRuntimeSize) => Promise<any>,
      put: (url: string, data?: any, headers?: any, runtimeSize?: ApiRuntimeSize) => Promise<any>,
      isOnline: () => Promise<boolean>,
      hotRead(enable: boolean)
    };
  }
}

async function stringHash(value: string) {
  return hasha.async(value, { algorithm: "sha1" });
}
const announcementModule = new AnnouncementModule();
const hotReadWindow = new HotReadWindow();
const instance = axios.create({
  baseURL: config.ST_API_BASE,
  withCredentials: false,
  headers: {
    Accept: "application/json",
  },
  // @ts-ignore
  useGlobalErrorHandler: true
});

// before a request is made start the nprogress
instance.interceptors.request.use(async (c) => {
  NProgress.start();
  let url = c.url;
  const query = qs.parse(location.search) as any;
  url=url?.replaceAll("{hotelId}", query.hotelId);
  url=url?.replaceAll("{outletId}", query.outletId);
  c.url=url;
  c.headers = c.headers || {};
  c.headers["token"] = await storage.getItem(config.STORAGE_KEYS.TOKEN);
  c.headers["sentec-announcement"] = AnnouncementApp.POS;
  c.headers["sentec-app"] = `pos${query.hotelId ? "-" + query.hotelId : ""}`;
  c.params = {
    ...c.params,
    origin: location.origin,
  };
  delete c.headers["key"];
  return c;
});
// before a response is returned stop nprogress
instance.interceptors.response.use(async (response) => {
  const writeMethods=["post","put","delete"];
  if (writeMethods.indexOf((response.config.method||"").toLocaleLowerCase())>-1){
    // Open (or extend) the read-from-write-replica window for subsequent GETs.
    hotReadWindow.open(WRITE_HOT_READ_WINDOW_MS);
  }
  NProgress.done();
  const config = response.config;
  const maxAge = config.headers["max-age"];
  const key = config.headers["key"];

  if (maxAge > 0) {
    let data="";
    if (config.data){
      data=config.data;
    }
    const rawKey = `${config.url}${config.method}${data}`;
    const hashKey = await stringHash(rawKey);
    await storage.setItem(hashKey, response.data);
  }
  if (key!="" && key!=null) {
    await storage.setItem(key, response.data);
  }
  if (config?.params?.["cache-ttl"] && config.params["cache-ttl"] > 0) {
    const expiry = new Date().getTime() + Number(config.params["cache-ttl"]);
    delete config.params["cache-ttl"];
    const tempParams = { ...config?.params };
    delete tempParams.origin;
    delete tempParams["from-write-server"];
    await storage.setItem(`${config?.url}?${new URLSearchParams(tempParams).toString()} ${config?.method}`, { value: response.data, expiry });
  }
  announcementModule.CheckIfServerMaintenance(response);
  return response;
}, async (error) => {
  NProgress.done();
  // check response header for new announcement
  announcementModule.CheckIfServerMaintenance(error.response);
  let message = "";
  // error handle from backend
  if (error.response && error.config.useGlobalErrorHandler) {
    const status = error.response.status;
    const rawData = error.response.data;

    const backendMessage = normalizeErrorMessage(rawData);

    if (status >= 400 && status <= 599 && backendMessage !== "") {
      message = backendMessage;
    }
    if (status >= 500) {
      if (status == 503) {
        message = "Service unavailable";
      } else {
        message = backendMessage !== "" ? backendMessage : (message !== "" ? message : "Something went wrong");
      }
    }
    app.state.info.textColor = "#E81123";
    app.state.info.icon = "fas fa-exclamation-circle";
    app.state.info.body = message;
    app.state.info.visible = true;
    if (status == 401) {
      Auth.SignOut().then(() => {
        window.location.reload();
      });
    }
  }
  if (message.indexOf("maintenance") > -1) {
    if (error.response.data && typeof error.response.data === "object") {
      error.response.data.Data = [];
    }
    return Promise.resolve(error);
  }

  return await Promise.reject(error);
});

export class ApiRuntimeSize {
  static Small: string="sm";
  static Medium: string="md";
  static Large: string="lg";
}

export const Api = {
  abortControllers:{} as Record<string, AbortController>,
  hotRead(enable:boolean){
    if (enable){
      hotReadWindow.open(MANUAL_HOT_READ_WINDOW_MS);
    } else {
      hotReadWindow.close();
    }
  },
  async isOnline(): Promise<boolean>{
    return await isReachable(config.IS_ONLINE_URL!);
  },
  async post(url: string, data?: any, header?: any, runtimeSize?: ApiRuntimeSize, ignoreGlobalErrorHandler?: boolean): Promise<any>{
    if (runtimeSize==null){
      runtimeSize=ApiRuntimeSize.Small;
    }
    url=`/${runtimeSize}${url}`;
    const online = await this.isOnline();
    if (!online){
      // PMS-1123: refuse rather than persist a credential. The queue keeps payloads —
      // including ones the server later rejects — in IndexedDB indefinitely, so a queued
      // void would leave a supervisor's PIN in plaintext on a shared terminal.
      if (payloadCarriesPin(data)) {
        return Promise.reject(new Error("This action needs a connection: it cannot be queued offline."));
      }

      const cacheKey="pending_POST";
      // GH-1322 / PMS-514: freeze the tenant context now, from the page that
      // issued the write. Persisting the tokenized URL would let the replay
      // (after reconnect) resolve {hotelId}/{outletId} against whatever page the
      // user is then on, silently misfiling the order into the wrong outlet.
      const resolvedUrl = resolveTenantUrl(url, qs.parse(location.search));
      if (resolvedUrl === null) {
        return Promise.reject(new Error("Cannot queue offline request: missing hotelId/outletId context"));
      }
      const cacheData = {
        Status: false,
        Message: "Offline"
      };
      const item = {
        url: resolvedUrl,
        data,
        timestamp: Math.floor(Date.now() / 1000),
        retryCount: 0,
        success: false,
        message: ""
      };

      const items: any = await storage.getItem(cacheKey) || [];
      items.push(item);
      await storage.setItem(cacheKey, items);
      return Promise.resolve({
        data: cacheData
      }
      );
    }
    let contentType = "application/json";
    if (header?.contentType) {
      contentType = header.contentType;
    } else if (data instanceof FormData) {
      contentType = "multipart/form-data";
    }

    const headers: Record<string, string> = {
      "max-age": String(header?.maxAgeInSecond || 0),
      "Content-Type": contentType,
    };
    // @ts-ignore
    return instance({
      url,
      method: "POST",
      headers,
      data,
      useGlobalErrorHandler: !ignoreGlobalErrorHandler
    });
  },
  async del(url: string, data?: any, header?: any,runtimeSize?: ApiRuntimeSize, ignoreGlobalErrorHandler?: boolean): Promise<any>{
    const online = await this.isOnline();
    if (runtimeSize==null){
      runtimeSize=ApiRuntimeSize.Small;
    }
    url=`/${runtimeSize}${url}`;
    if ((header?.maxAgeInSecond || 3600) > 0 && !online){
      const rawKey=`${url}del${JSON.stringify(data)}`;
      const hashKey = await stringHash(rawKey);
      const cacheData = await storage.getItem(hashKey);
      return Promise.resolve({
        data: cacheData
      }
      );
    }
    const headers: Record<string, string> = {
      "max-age": String(header?.maxAgeInSecond || 0),
      "Content-Type": header?.contentType || "application/json",
    };
    // @ts-ignore
    return instance({
      url,
      method: "DELETE",
      headers,
      data,
      useGlobalErrorHandler: !ignoreGlobalErrorHandler
    });
  },
  async put(url: string, data?: any, maxAgeInSecond=0,runtimeSize?: ApiRuntimeSize): Promise<any>{
    const online = await this.isOnline();
    if (runtimeSize==null){
      runtimeSize=ApiRuntimeSize.Small;
    }
    url=`/${runtimeSize}${url}`;
    if (!online){
      // PMS-1123: same refusal as post() — the PUT queue persists payloads too, and the
      // exclusion is stated over the payload rather than over the set of URLs that happen
      // to send a PIN today.
      if (payloadCarriesPin(data)) {
        return Promise.reject(new Error("This action needs a connection: it cannot be queued offline."));
      }

      const cacheKey="pending_PUT";
      // GH-1322 / PMS-514: freeze the tenant context at enqueue time (see post()).
      const resolvedUrl = resolveTenantUrl(url, qs.parse(location.search));
      if (resolvedUrl === null) {
        return Promise.reject(new Error("Cannot queue offline request: missing hotelId/outletId context"));
      }
      const cacheData = {
        Status: false,
        Message: "Offline",
      };

      const item = {
        url: resolvedUrl,
        data,
        timestamp: Math.floor(Date.now() / 1000),
        retryCount: 0,
        success: false,
        message: ""
      };

      const items: any = await storage.getItem(cacheKey) || [];
      items.push(item);
      await storage.setItem(cacheKey, items);
      return Promise.resolve({
        data: cacheData
      }
      );
    }
    const headers: Record<string, string> = {
      "max-age": String(maxAgeInSecond)
    };
    const config={
      headers
    };
    return instance.put(url, data, config);
  },
  async get(
    url: string,
    params?: any,
    header?: any,
    runtimeSize?: ApiRuntimeSize,
    skipCancellation?: boolean,
    overrideBaseUrl?: string,
    ignoreGlobalErrorHandler?: boolean): Promise<any>{
    if (runtimeSize==null){
      runtimeSize=ApiRuntimeSize.Small;
    }
    if (params?.["cache-ttl"] && params["cache-ttl"] > 0) {
      const query = qs.parse(location.search) as any;
      let tempUrl=url?.replaceAll("{hotelId}", query.hotelId);
      tempUrl=tempUrl?.replaceAll("{outletId}", query.outletId);
      const tempParams = { ...params };
      delete tempParams["cache-ttl"];
      const key = `/${runtimeSize}${tempUrl}?${new URLSearchParams(tempParams).toString()} get`;
      const cacheData: any = await storage.getItem(key);
      const now = new Date().getTime();
      if (cacheData && now < cacheData.expiry) {
        return new Promise((res,rej) => {
          return res({ data: cacheData.value });
        });
      } else if (cacheData && now >= cacheData.expiry) {
        await storage.removeItem(key);
      }
    }
    // original : url=`/${runtimeSize}${url}`;
    // add override base url feature
    url = `${overrideBaseUrl ? overrideBaseUrl : ""}/${runtimeSize}${url}`;
    const online = await this.isOnline();
    if (!online){
      const rawKey=`${url}get`;
      const hashKey = await stringHash(rawKey);
      const cacheData = await storage.getItem(hashKey);
      return Promise.resolve({
        data: cacheData,
      }
      );
    }
    const basePath= url.split("?")[0];
    const headers: Record<string, string> = {
      "max-age": String(header?.maxAgeInSecond ?? 5),
      "Content-Type": header?.contentType || "application/json",
    };
    if (hotReadWindow.isOpen()){
      if (params==null){
        params={};
      }
      params["from-write-server"]=1;
    }
    const config: AxiosRequestConfig & { useGlobalErrorHandler?: boolean } = {
      url,
      method: "GET",
      params,
      headers
    };
    if(skipCancellation!==true){
      const newAbortController = new AbortController();
      const prevAbortController = this.abortControllers[basePath];
      if(prevAbortController){
        prevAbortController.abort();
      }
      config.signal = newAbortController.signal;
      config.useGlobalErrorHandler = !ignoreGlobalErrorHandler;
      this.abortControllers[basePath]=newAbortController;
    }
    return instance(config);
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  async postv2(param: ApiCallParameter): Promise<any> {
    // not implemented, just to fullfil the GenericCrudService interface
    throw new Error("not implemented");
  },

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
  async postDownload(url: string, data?: any, header?: any, runtimeSize?: ApiRuntimeSize, ignoreGlobalErrorHandler?: boolean): Promise<any> {
    // not implemented, just to fullfil the GenericCrudService interface
    throw new Error("not implemented");
  },
};
export default function AxiosPlugin(Vue: VueConstructor): void {
  Vue.prototype.$http = Api;
}
